import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box, Heading } from '@primer/react';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Caption = makeShortcode("Caption");
const Dont = makeShortcode("Dont");
const Note = makeShortcode("Note");
const CustomVideoPlayer = makeShortcode("CustomVideoPlayer");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2>{`Form control anatomy`}</h2>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/171692166-43d45c4b-509a-4f68-9e8b-1577658b493e.png",
        "alt": "diagrams labeling the anatomy of a text field and a checkbox field"
      }}></img></p>
    <h3>{`Label (required)`}</h3>
    <p>{`Labels should be descriptive and concise: aim for no more than 3 words. Write labels in sentence case. Examples: "Repository name", "Payment method".`}</p>
    <p>{`If you're having trouble keeping label text short, consider using a `}<a parentName="p" {...{
        "href": "#caption"
      }}>{`caption`}</a>{` to provide more context.`}</p>
    <p>{`Placeholder text is never an acceptable substitute for a label because:`}</p>
    <ul>
      <li parentName="ul">{`The placeholder text disappears as soon as the input has a value`}</li>
      <li parentName="ul">{`Placeholder text colors are typically too light to meet the minimum color contrast ratio required for accessibility`}</li>
      <li parentName="ul">{`Screen readers do not read placeholder text as a label`}</li>
    </ul>
    <h3>{`Required field indicator`}</h3>
    <p>{`When a field is required to have a value, it should be visibly marked as required. An individual checkbox or radio button cannot be marked as required.`}</p>
    <h3>{`Input (required)`}</h3>
    <p>{`The input is what the user interacts with to set the value of the form control. You may pre-fill inputs with smart default values, but be careful about making too many assumptions about what a user wants or needs.`}</p>
    <h3>{`Caption`}</h3>
    <p>{`A caption may be used to provide additional context about the field to help users fill in the correct data or explain how the data will be used. Caption text should be as short as possible.`}</p>
    <p>{`Caption text may be displayed alongside a validation message, or it may be hidden if it only provides redundant information.`}</p>
    <p>{`Caption text may be used to augment the `}<a parentName="p" {...{
        "href": "#label-required"
      }}>{`label`}</a>{`, but should not be redundant with the label or any other parts of the form control. If the caption feels redundant, try removing it.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069904-5ff41e86-a95f-4644-b1af-1b8a6225dc59.png" />
    <Caption mdxType="Caption">Use caption text that shows new information and provides helpful context</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069907-5a147b22-0856-4c61-91a2-f9c1db8e941d.png" />
    <Caption mdxType="Caption">Don’t use caption text that is redundant</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Validation message`}</h3>
    <p>{`A single validation message may be displayed to provide helpful information for a user to complete their task. For example: explain why a value is invalid so they can correct it and submit the form.`}</p>
    <p>{`An individual checkbox or radio should not have its own validation message or style.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069916-08bcf5c6-9a8f-4998-a246-39d7f020e94b.png" />
    <Caption mdxType="Caption">Show a validation message for the group of inputs</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069919-d557c4d8-ed4d-4a3c-80c7-3372359603ab.png" />
    <Caption mdxType="Caption">Don’t show a validation message for each input</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`Information from the caption should not be repeated in the error message. Show the validation message and remove the caption. A validation message makes the field easier to spot when a user is scanning for invalid fields.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069909-b9ad91e4-db88-42c0-bc83-d2f00f24a66e.png" />
    <Caption mdxType="Caption">Hide a caption that is redundant with the validation message</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069912-d8dfdde4-08bd-47ab-b9fe-3ab0f0f603a5.png" />
    <Caption mdxType="Caption">Don’t show a repetitive validation message</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`For more information about form validation, see the `}<a parentName="p" {...{
        "href": "#validation"
      }}>{`validation guidelines`}</a>{`.`}</p>
    <h2>{`Input methods`}</h2>
    <h3>{`Open-ended text`}</h3>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/170069913-325a987b-9a75-4e83-826a-bb7ae73d6831.png",
        "alt": "Text input, open-ended autocomplete text input, plain text input, textarea"
      }}></img></p>
    <p>{`Use an open-ended text field when the field does not have a list of possible values. If the input is able to suggest values, use `}<a parentName="p" {...{
        "href": "/components/avatar-pair"
      }}>{`autocomplete`}</a>{` to allow users to pick a value or enter their own.`}</p>
    <h3>{`A set of selectable options`}</h3>
    <p><img parentName="p" {...{
        "src": "https://user-images.githubusercontent.com/2313998/170069915-657a6291-2729-4e3e-abbc-b7611f8051dc.png",
        "alt": "Action menu, autocomplete input with limited options, checkbox group, radio group, select"
      }}></img></p>
    <p>{`Show a set of selectable options when there is a finite number of possible values.`}</p>
    <h2>{`Structure`}</h2>
    <p>{`Forms should have a structure that makes it easy for users to scan. Forms that flow vertically are easier for sighted users to scan visually.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069921-98bb9035-2125-4807-b790-1da90e6bde3b.png" />
    <Caption mdxType="Caption">Default to vertically stacked form controls</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img role="presentation" src="https://user-images.githubusercontent.com/2313998/170069922-ed8a0ef7-0573-4f02-bd15-da6a5acbc527.png" />
    <Caption mdxType="Caption">Don’t lay out forms that flow into columns just to reduce the vertical space used by the form</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Order of form controls`}</h3>
    <p>{`Form fields should be in a predictable order that flows intuitively.`}</p>
    <p>{`To achieve an intuitive flow:`}</p>
    <ul>
      <li parentName="ul">{`Order fields by their relative level of importance`}</li>
      <li parentName="ul">{`Keep related fields near each other`}</li>
      <li parentName="ul">{`When possible, keep inputs that require keyboard input near each other so that users who use a mouse don't have to switch back and forth between clicking and typing`}</li>
    </ul>
    <h3>{`Sizing form controls`}</h3>
    <h4>{`Hint at the expected length of the value`}</h4>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" role="presentation" src="https://user-images.githubusercontent.com/2313998/170069941-0e7ead2f-1e20-47dc-b7b0-e7a27480d2a5.png" />
  <Box mdxType="Box">
    <Box as="p" mt="0" mdxType="Box">
      Form controls should be sized to fit their value. Start with the browser default width, and adjust as needed.
    </Box>
    <Box as="ul" pl="1rem" mdxType="Box">
      <li>
        Single-line text inputs get a default width set by the browser, but their width can be changed to fit the
        approximate length of their text.
        <ul>
          <li>
            If the text length could fall into a wide range, then either keep the browser-default length, or fill the
            width of its parent.
          </li>
        </ul>
      </li>
      <li>
        Multi-line text inputs <a href="#textarea">Textarea</a> get a default width and height set by the browser, but
        its dimensions can be changed to fit longer blocks of text.
      </li>
      <li>
        Select dropdown widths are set by the browser to be as wide as the text of it's longest option. We shouldn't
        override this width unless it's to make the dropdown fill the width of its parent.
      </li>
      <li>Checkbox and radio inputs have static dimensions</li>
    </Box>
  </Box>
    </Box>
    <h4>{`Maintain page hierarchy`}</h4>
    <p>{`Input components ship with size variants that adjust font size and padding to maintain consistent hierarchy with adjacent elements.`}</p>
    <h3>{`Grouping form controls`}</h3>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" alt="visually grouped of related inputs" src="https://user-images.githubusercontent.com/2313998/170069926-230f01ae-6371-4b4b-9b79-c2a82a7f5d2e.png" />
  <Box as="p" mt="0" mdxType="Box">
    When there is a collection of closely related fields, they should be labeled and visually grouped together. For
    example: putting form controls closer together.
  </Box>
    </Box>
    <h3>{`Progressively disclosed form controls`}</h3>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" alt="form section where controls are hidden until the feature is enabled" src="https://user-images.githubusercontent.com/2313998/170069928-5ea261f4-78c1-44d6-92f4-95392d12b47f.png" />
  <Box as="p" mt="0" mdxType="Box">
    To keep forms concise, you may choose to hide or show form controls based on selections the user has made.
  </Box>
    </Box>
    <h4>{`Nested form controls`}</h4>
    <p>{`Sometimes, progressively disclosed form controls can be visually nested under a parent form control. A common pattern is to use a checkbox or radio “checked” state to decide whether to show a related form control.`}</p>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" alt="Primary discipline radio buttons, selected 'Other' option reveals a text input" src="https://user-images.githubusercontent.com/2313998/170069930-87ffd7f3-89db-4a0a-bca3-64d0244e54a3.png" />
  <Box as="p" mt="0" mdxType="Box">
    If the parent form control provides sufficient visual context, you may visually hide the label. However, you must
    specify text for a visually hidden label that is accessible to screen readers.
  </Box>
    </Box>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flexGrow={1} flexShrink={0} flexBasis={['column', 'column', 'column', 'column', '456px']} mdxType="Box">
    <img width="456" alt="Checkbox that progressively discloses a dropdown with an internal label" src="https://user-images.githubusercontent.com/2313998/170069931-24cf2caa-148d-49bd-b1dc-361ec1ea8b61.png" />
    <img width="456" alt="Checkbox that progressively discloses a labeled select input" src="https://user-images.githubusercontent.com/2313998/170069932-37f1d07d-f7ef-4021-b74c-f217a990296f.png" />
  </Box>
  <Box as="p" mt="0" flexShrink={1} mdxType="Box">
    If the parent form control does not provide sufficient context, a visible label should be shown. Make sure the label
    of the nested form control doesn’t clash with the label of its parent form control.
  </Box>
    </Box>
    <p>{`See `}<a parentName="p" {...{
        "href": "/ui-patterns/progressive-disclosure"
      }}>{`progressive disclosure`}</a>{` for more information.`}</p>
    <h2>{`Validation`}</h2>
    <p>{`Use Primer instead of browser-native validation UI. Browser-native validation messages are not accessible to screen readers, and they visually clash with Primer styles.`}</p>
    <p>{`Disabled buttons are discouraged, as they don't clearly communicate what actions a user should take to complete a form.`}</p>
    <p>{`Instead, consider the following validation methods.`}</p>
    <h3>{`Validation statuses`}</h3>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={["column", "column", "column", "column", "row"]} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flexGrow={1} flexBasis="0" mdxType="Box">
    <img alt="input with forbidden characters" src="https://user-images.githubusercontent.com/2313998/170069933-233fd82d-7af8-4475-b73c-65bec80a3891.png" />
    <Box as="p" mt="0" mdxType="Box"><strong>Error messages:</strong> An invalid field should always have a message explaining why the value does not pass validation. The message should explain why the value is invalid, and unblock users from completing their task by guiding them to a valid value.</Box>
  </Box>
  <Box flexGrow={1} flexBasis="0" mdxType="Box">
    <img alt="valid repo name success" src="https://user-images.githubusercontent.com/2313998/170069936-48ddd7de-9cdb-4c18-bccd-18fa4a39bcf0.png" />
    <Box as="p" mt="0" mdxType="Box"><strong>Success messages:</strong> A success message may be used when a user might need extra assurance that the field's value is valid. For example: when creating a repository name, it’s nice to be assured that the name is available and valid.</Box>
  </Box>
    </Box>
    <h3>{`Validation on submit`}</h3>
    <p>{`The default behavior of the web is to perform validation when the user attempts to submit the form. This lets the user flow quickly through the form without interruption.`}</p>
    <p>{`When the form fails validation, guide the user to the invalid inputs:`}</p>
    <ul>
      <li parentName="ul">{`If the form has 3 or more errors, you may show an `}<a parentName="li" {...{
          "href": "#interactive-summary-of-errors"
        }}>{`interactive summary of errors`}</a></li>
      <li parentName="ul">{`If an interactive summary of errors is not shown, the first invalid input should be focused and scrolled into the viewport`}</li>
    </ul>
    <p>{`After a form has been submitted and failed validation, you may switch to inline validation to provide quicker feedback.`}</p>
    <h4>{`Interactive summary of errors`}</h4>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" alt="a form for biographical information that has three invalid inputs" src="https://user-images.githubusercontent.com/2313998/170069940-44aec752-e840-4e51-abe5-fd45226f0388.png" />
  <div>
    <Box as="p" mt="0" mdxType="Box">
      In a <a href="/design/ui-patterns/messaging#flash-alerts">flash alert</a> at the top of the form, list the invalid
      inputs as anchor links. When the link is activated, place focus in it's corresponding input.
    </Box>
    <Box as="p" mt="0" mdxType="Box">
      When the flash alert appears, it should be focused.
    </Box>
  </div>
    </Box>
    <h3>{`Inline validation`}</h3>
    <Note variant="warning" mdxType="Note">
  Inline validation offers immediate feedback, but be sure to consider the following drawbacks.
  <ul>
    <li>There is a negative impact on perceived performance when validating server-side.</li>
    <li>When a screen reader user moves focus from the invalid input to the next form control, they will be interrupted by the validation message of the previous form control.</li>
  </ul>
    </Note>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flexShrink={0} mdxType="Box">
    <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/170273001-3e722629-bcb3-4677-83b5-cfde9296ca52.mp4" mdxType="CustomVideoPlayer" />
  </Box>
  <Box as="p" mt="0" mdxType="Box">
    If the form control is in a valid state, validation should be performed until after the user has made a change to
    the input <strong>and has removed focus from the input</strong>.
  </Box>
    </Box>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flexShrink={0} mdxType="Box">
    <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/170273004-913ba77a-041f-43e5-9809-0d2c303cf518.mp4" mdxType="CustomVideoPlayer" />
  </Box>
  <Box as="p" mt="0" mdxType="Box">
    Don't attempt to validate an input before the user is done with it. Validation may be performed as the user is
    typing or making their selection, but only <strong>after the first time the input has been validated</strong> and
    the <strong>input is in an invalid state</strong>. This gives the user early positive feedback by removing the error
    if the user makes a change that fixes it.
  </Box>
    </Box>
    <Box mb={3} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flexShrink={0} mdxType="Box">
    <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/170273002-677fea75-265a-42d7-b8e8-f5a07128ca38.mp4" mdxType="CustomVideoPlayer" />
  </Box>
  <Box as="p" mt="0" mdxType="Box">
    If the form control’s validation is likely to take more than 1 second, show a loading indicator.
  </Box>
    </Box>
    <h2>{`Submission`}</h2>
    <p>{`Forms should follow consistent patterns for `}<a parentName="p" {...{
        "href": "/ui-patterns/saving"
      }}>{`submitting and saving data`}</a>{`.`}</p>
    <h2>{`Related components`}</h2>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" role="presentation" src="https://user-images.githubusercontent.com/293280/123881067-cdbaea00-d8f8-11eb-98e4-e57c64489308.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Action menu with selection</Heading>
    <p>
      If the parent form control provides sufficient visual context, you may visually hide the label. However, you must
      specify text for a visually hidden label that is accessible to screen readers.
    </p>
    <a href="https://primer.style/react/ActionMenu#with-selection">Primer React implementation</a>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268364-021748a9-f304-4626-8ff2-3a71a5bcbd6b.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Autocomplete</Heading>
    <p>
      An autocomplete input renders a text input that allows a user to quickly filter through a list of options to pick
      one or more values.
    </p>
    <Box display="flex" sx={{
          gap: 3
        }} mdxType="Box">
      <a href="../components/autocomplete">Interface guidelines</a>
      <span aria-hidden="true">|</span>
      <a href="https://primer.style/react/Autocomplete">Primer React implementation</a>
    </Box>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268367-51ee68d4-8121-4f49-b995-86755a381d6c.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Checkbox group</Heading>
    <p>A set of checkboxes to let users make one or more selections from a short list of options</p>
    <Box display="flex" sx={{
          gap: 3
        }} mdxType="Box">
      <a href="../components/checkbox-group">Interface guidelines</a>
      <span aria-hidden="true">|</span>
      <a href="https://primer.style/react/CheckboxGroup">Primer React implementation</a>
    </Box>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268368-5daef93f-b7d3-4863-938a-9c777c609ee4.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Form control</Heading>
    <p>A form control renders a labelled input and, optionally, associated validation text and/or hint text.</p>
    <Box display="flex" sx={{
          gap: 3
        }} mdxType="Box">
      <a href="../components/form-control">Interface guidelines</a>
      <span aria-hidden="true">|</span>
      <a href="https://primer.style/react/FormControl">Primer React implementation</a>
    </Box>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268369-b9885114-90f0-4bae-863e-ca3ed37aa67d.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Radio group</Heading>
    <p>A set of radio inputs to let users make a single selection from a short list of options</p>
    <Box display="flex" sx={{
          gap: 3
        }} mdxType="Box">
      <a href="../components/radio-group">Interface guidelines</a>
      <span aria-hidden="true">|</span>
      <a href="https://primer.style/react/RadioGroup">Primer React implementation</a>
    </Box>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268372-be76e525-7337-4739-afd0-7e4a12037731.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Select dropdown</Heading>
    <p>A select input may be used when a user needs to select one option from a long list</p>
    <a href="https://primer.style/react/Select">Primer React implementation</a>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268373-463d55fa-ead8-4471-91f2-2bec2456f385.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} id="textarea" mdxType="Heading">
      Textarea
    </Heading>
    <p>A text area is used to put multiple lines of text in an input</p>
    <a href="https://primer.style/react/Textarea">Primer React implementation</a>
  </div>
    </Box>
    <Box mb={4} display="flex" alignItems="flex-start" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img role="presentation" width="456" src="https://user-images.githubusercontent.com/2313998/170268374-7d701912-5f6c-45ce-832f-e7c3b1d3c476.png" />
  <div>
    <Heading sx={{
          fontSize: 3
        }} mdxType="Heading">Text input</Heading>
    <p>
      A text input is used to set a value that is a single line of text. See the list of{' '}
      <a href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types">types in the MDN docs</a>.
    </p>
    <Box display="flex" sx={{
          gap: 3
        }} mdxType="Box">
      <a href="../components/text-input">Interface guidelines</a>
      <span aria-hidden="true">|</span>
      <a href="https://primer.style/react/TextInput">Primer React implementation</a>
    </Box>
  </div>
    </Box>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      